import type {
  ComparisonDetail,
  ComparisonObservation,
  ComparisonFilters,
} from "../../domain/insights/comparison-types.js";

export type RivalInsightContext =
  | { kind: "phase"; phase: keyof ComparisonDetail["phases"] }
  | { kind: "hole"; courseId: string; teePair: string; holeNumber: number }
  | { kind: "condition"; value: "weather-rain" | "both-cart" | "both-walking" };

export interface RivalInsight {
  visual?: {
    title: string;
    names: [string, string];
    values: [number, number];
    unit: string;
    sample: string;
  };
  text: string;
  basis: string;
  relationId: string;
  sourceRoundIds: string[];
  context: RivalInsightContext;
  filters: ComparisonFilters;
}
type Candidate = RivalInsight & { score: number; detail: ComparisonDetail };

/** Keep repeated evidence first, but give each rivalry a place before repeating one. */
export function chooseRivalInsights(
  details: ComparisonDetail[],
  limit = 12,
): RivalInsight[] {
  const candidates = details
    .flatMap(candidatesFor)
    .sort((a, b) => b.score - a.score || a.text.localeCompare(b.text));
  const groups = new Map<string, Candidate[]>();
  for (const candidate of candidates)
    groups.set(candidate.relationId, [
      ...(groups.get(candidate.relationId) ?? []),
      candidate,
    ]);
  const result: RivalInsight[] = [];
  while (
    result.length < limit &&
    [...groups.values()].some((items) => items.length)
  ) {
    for (const items of groups.values()) {
      const next = items.shift();
      if (next && result.length < limit) result.push(findingOf(next));
    }
  }
  return result;
}

/** Deterministic relevance: repeated personal evidence outranks an isolated observation. */
export function chooseRivalInsight(
  details: ComparisonDetail[],
): { finding: RivalInsight; detail: ComparisonDetail } | null {
  const candidates = details.flatMap(candidatesFor);
  const best = candidates.sort(
    (a, b) =>
      b.score - a.score ||
      (b.detail.relation.latestPlayedAt ?? "").localeCompare(
        a.detail.relation.latestPlayedAt ?? "",
      ) ||
      a.text.localeCompare(b.text),
  )[0];
  if (!best) return null;
  return { finding: findingOf(best), detail: best.detail };
}

/** Re-resolve the selected subject from authorized, current evidence after navigation. */
export function rivalInsightForContext(
  detail: ComparisonDetail,
  context: RivalInsightContext,
): RivalInsight | null {
  const candidate = candidatesFor(detail).find((item) =>
    sameContext(item.context, context),
  );
  if (!candidate) return null;
  return findingOf(candidate);
}

function findingOf(candidate: Candidate): RivalInsight {
  const { text, basis, relationId, sourceRoundIds, context, filters, visual } =
    candidate;
  return {
    text,
    basis,
    relationId,
    sourceRoundIds,
    context,
    filters,
    ...(visual ? { visual } : {}),
  };
}

function sameContext(a: RivalInsightContext, b: RivalInsightContext): boolean {
  if (a.kind === "phase" && b.kind === "phase") return a.phase === b.phase;
  if (a.kind === "condition" && b.kind === "condition")
    return a.value === b.value;
  return (
    a.kind === "hole" &&
    b.kind === "hole" &&
    a.courseId === b.courseId &&
    a.teePair === b.teePair &&
    a.holeNumber === b.holeNumber
  );
}

export function rivalInsightFilters(
  finding: Pick<RivalInsight, "filters" | "context">,
): ComparisonFilters {
  const { context } = finding;
  if (context.kind === "hole")
    return {
      ...finding.filters,
      courseId: context.courseId,
      teePair: context.teePair,
    };
  if (context.kind === "condition")
    return {
      ...finding.filters,
      ...(context.value === "weather-rain"
        ? { weather: "rain" as const }
        : { transport: context.value }),
    };
  return { ...finding.filters };
}
function candidatesFor(detail: ComparisonDetail): Candidate[] {
  const items: Candidate[] = [];
  for (const [key, label] of [
    ["opening", "the opening hole"],
    ["front9", "the first nine"],
    ["back9", "the last nine"],
    ["closing3", "the closing three holes"],
  ] as const) {
    const candidate = observationCandidate(
      detail,
      detail.phases[key],
      label,
      key === "opening" ? 3 : 1,
      { kind: "phase", phase: key },
    );
    if (candidate) items.push(candidate);
  }
  for (const hole of detail.holes) {
    if (hole.observation.sampleRounds < 3) continue;
    const candidate = observationCandidate(
      detail,
      hole.observation,
      `hole ${hole.holeNumber} at ${hole.courseName}`,
      4,
      {
        kind: "hole",
        courseId: hole.courseId,
        teePair: hole.teePair,
        holeNumber: hole.holeNumber,
      },
    );
    if (candidate) items.push(candidate);
  }
  for (const condition of detail.conditions) {
    const labels: Record<string, string> = {
      "weather-rain": "in recorded rain",
      "both-cart": "when both used a cart",
      "both-walking": "when both walked",
    };
    const label = labels[condition.value];
    const record = condition.record;
    if (!label || record.roundCount < 3 || record.leftWins === record.rightWins)
      continue;
    const side = record.leftWins > record.rightWins ? "left" : "right";
    const wins = record[`${side}Wins`];
    if (wins / record.contestCount < 0.6) continue;
    items.push({
      detail,
      relationId: detail.relation.id,
      sourceRoundIds: condition.sourceRoundIds,
      context: {
        kind: "condition",
        value: condition.value as "weather-rain" | "both-cart" | "both-walking",
      },
      filters: detail.filters,
      visual: insightVisual(
        detail,
        label.replace(/^when /, ""),
        record.leftWins,
        record.rightWins,
        "wins",
        record.roundCount,
      ),
      text: `${detail.relation[side].name} won ${wins} of ${record.contestCount} agreed contests ${label}.`,
      basis: `${record.roundCount} shared rounds · recorded conditions, not a prediction`,
      score:
        Math.min(record.roundCount, 12) * 2 +
        (10 * wins) / record.contestCount +
        2,
    });
  }
  return items;
}
function observationCandidate(
  detail: ComparisonDetail,
  item: ComparisonObservation,
  label: string,
  weight: number,
  context: RivalInsightContext,
): Candidate | null {
  if (!item.n || item.leftWins === item.rightWins) return null;
  const side = item.leftWins > item.rightWins ? "left" : "right";
  const wins = item[`${side}Wins`];
  if (item.sampleRounds >= 3 && wins / item.n < 0.6) return null;
  const basis = detail.filters.basis === "net" ? "net" : "gross";
  return {
    detail,
    relationId: detail.relation.id,
    context,
    filters: detail.filters,
    visual: insightVisual(
      detail,
      label.replace(/^the /, ""),
      item.leftWins,
      item.rightWins,
      `${basis} · ahead`,
      item.sampleRounds,
    ),
    text: `${detail.relation[side].name} had the lower ${basis} score on ${label} in ${wins} of ${item.n} recorded comparisons.`,
    basis: `${item.sampleRounds} shared ${item.sampleRounds === 1 ? "round" : "rounds"} · ${item.smallSample ? "early evidence" : "recorded history"}${item.missing ? ` · ${item.missing} missing excluded` : ""}`,
    sourceRoundIds: [...new Set(item.evidence.map((e) => e.roundId))],
    score:
      (item.sampleRounds < 3 ? -20 : 0) +
      Math.min(item.sampleRounds, 12) * 2 +
      (10 * wins) / item.n +
      weight,
  };
}

function insightVisual(
  detail: ComparisonDetail,
  title: string,
  left: number,
  right: number,
  unit: string,
  rounds: number,
): NonNullable<RivalInsight["visual"]> {
  const ownFirst = detail.relation.viewerSide === "left";
  return {
    title: title[0]!.toUpperCase() + title.slice(1),
    names: ownFirst
      ? [detail.relation.left.name, detail.relation.right.name]
      : [detail.relation.right.name, detail.relation.left.name],
    values: ownFirst ? [left, right] : [right, left],
    unit,
    sample: `${rounds} shared ${rounds === 1 ? "round" : "rounds"}${rounds < 3 ? " · early record" : ""}`,
  };
}
